import React, { useState } from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiSearch, FiTrendingUp, FiInfo } = FiIcons

const KeywordInput = ({ onSearch, loading, userCredits, onPurchaseClick }) => {
  const [keyword, setKeyword] = useState('')
  const [searchMode, setSearchMode] = useState('broad')
  const [showBroadTooltip, setShowBroadTooltip] = useState(false)
  const [showCustomTooltip, setShowCustomTooltip] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (keyword.trim()) {
      onSearch(keyword.trim(), searchMode)
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-2 mb-8 dark:bg-gray-800">
      <form onSubmit={handleSubmit}>
        {/* Improved desktop layout with better spacing */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Header section with fixed width on larger screens */}
          <div className="flex flex-col sm:flex-row items-center lg:w-1/3 gap-4 mb-4 lg:mb-0">
            {/* Icon */}
            <div className="bg-[#D59A4C30] p-3 rounded-full flex-shrink-0">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-[#D59A4C]" />
            </div>
            {/* Header Text */}
            <div className="flex-shrink-0 text-center sm:text-left">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white whitespace-nowrap">
                Discover Backlink Opportunities
              </h2>
            </div>
          </div>
          {/* Search Input and Submit Button Container - full width on mobile, appropriate width on desktop */}
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-2/3">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={searchMode === 'custom' ? 'Enter your custom footprint...' : 'Enter your keyword...'}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                disabled={loading}
              />
              <SafeIcon icon={FiSearch} className="absolute left-4 top-3.5 text-gray-400 w-4 h-4 dark:text-gray-500" />
              <div className="mt-2">
                <div className="flex flex-col sm:flex-row gap-4">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 py-[1px]">
                    <input
                      type="radio"
                      name="searchMode"
                      value="broad"
                      checked={searchMode === 'broad'}
                      onChange={() => setSearchMode('broad')}
                      className="text-[#D59A4C]"
                    />
                    <span><b>Broad Search</b>: 0.5 credits / 100 results</span>
                    <div className="relative">
                      <div
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none no-border"
                        onMouseEnter={() => setShowBroadTooltip(true)}
                        onMouseLeave={() => setShowBroadTooltip(false)}
                        onClick={() => setShowBroadTooltip((v) => !v)}
                      >
                        <SafeIcon icon={FiInfo} className="w-4 h-4 text-[#D59A4C]" />
                      </div>
                      {showBroadTooltip && (
                        <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-10 max-w-[calc(100vw-2rem)] sm:absolute sm:left-0 sm:top-full sm:bottom-auto sm:translate-x-0 sm:mt-2 sm:w-72 bg-white dark:bg-gray-800 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-sm text-left break-words">
                          <b>Spend 0.5 credits and get 100 backlink opportunities in total for your keyword.</b>
                          <p>Get 100 opportunities across 5 linkbuilding types: Niche Link Inserts, Blog Comments, Guest Posts, Forum Comments, and Resource Pages. Just enter a keyword related to your niche and/or location.</p>
                        </div>
                      )}
                    </div>
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 py-[1px]">
                    <input
                      type="radio"
                      name="searchMode"
                      value="custom"
                      checked={searchMode === 'custom'}
                      onChange={() => setSearchMode('custom')}
                      className="text-[#D59A4C]"
                    />
                    <span><b>Custom Search</b>: 0.1 credits / 20 results</span>
                    <div className="relative">
                      <div
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none no-border"
                        onMouseEnter={() => setShowCustomTooltip(true)}
                        onMouseLeave={() => setShowCustomTooltip(false)}
                        onClick={() => setShowCustomTooltip((v) => !v)}
                      >
                        <SafeIcon icon={FiInfo} className="w-4 h-4 text-[#D59A4C]" />
                      </div>
                      {showCustomTooltip && (
                        <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-10 max-w-[calc(100vw-2rem)] sm:absolute sm:left-0 sm:top-full sm:bottom-auto sm:translate-x-0 sm:mt-2 sm:w-72 bg-white dark:bg-gray-800 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-sm text-left break-words">
                          <b>Spend 0.1 credits and get 20 opportunities in total for 1 custom footprint.</b>
                          <p>Ideal for advanced SEO users. Enter a search operator targeting your desired link type + keyword tied to your niche or location. Chat with our AI for SEO footprint ideas to find top backlink opportunities.</p>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>
            {/* Submit Button - full width on mobile */}
            <motion.button
              type="submit"
              disabled={loading || !keyword.trim()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-[#D59A4C] text-white px-4 rounded-xl hover:bg-[#B58C54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold whitespace-nowrap w-full sm:w-auto border border-transparent h-[42px]"
            >
              {loading ? 'Searching...' : 'Find Backlinks'}
            </motion.button>
          </div>
        </div>
      </form>
      {userCredits === 0 && (
        <div className="text-center mt-4 bg-red-50 border border-red-200 rounded-md px-4 py-2 text-sm text-red-600 dark:bg-red-900/30 dark:border-red-800 dark:text-red-400">
          No credits remaining
        </div>
      )}
    </div>
  )
}

export default KeywordInput
